import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Seo from "../components/Seo";
import { adminListMedia, adminUploadMedia, adminDeleteMedia, adminMe, mediaUrl } from "../lib/api";
import { AdminHeader } from "../components/AdminNav";

export default function AdminMedia() {
    const [media, setMedia] = useState(null);
    const [query, setQuery] = useState("");
    const [sourceFilter, setSourceFilter] = useState("");
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [error, setError] = useState("");
    const [dragOver, setDragOver] = useState(false);
    const fileInputRef = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
        adminMe().catch(() => navigate("/admin/login", { replace: true }));
    }, [navigate]);

    const load = useCallback(() => {
        const params = {};
        if (query) params.q = query;
        if (sourceFilter) params.source = sourceFilter;
        adminListMedia(params).then(setMedia).catch((err) => setError(err?.message || "Load failed"));
    }, [query, sourceFilter]);

    useEffect(() => { load(); }, [load]);

    const upload = async (files) => {
        if (!files || !files.length) return;
        setUploading(true);
        setProgress(0);
        setError("");
        try {
            await adminUploadMedia(Array.from(files), (e) => {
                if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
            });
            load();
        } catch (err) {
            setError(err?.response?.data?.detail || "Upload failed");
        } finally {
            setUploading(false);
            setProgress(0);
        }
    };

    const onDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        upload(e.dataTransfer.files);
    };

    const onDelete = async (item) => {
        if (!window.confirm(`Delete "${item.filename}"?`)) return;
        try {
            await adminDeleteMedia(item.id);
            load();
        } catch (err) {
            setError(err?.response?.data?.detail || "Delete failed");
        }
    };

    return (
        <main data-testid="page-admin-media" className="min-h-screen bg-[#F9F8F6] px-6 md:px-12 lg:px-24 py-16 md:py-24">
            <Seo path="/admin/media" title="Media Library — Admin" description="Manage media" noindex />
            <AdminHeader title="Media Library" />

            <div
                data-testid="media-dropzone"
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={onDrop}
                onClick={() => fileInputRef.current?.click()}
                className={`mt-8 border-2 border-dashed cursor-pointer transition-colors ${dragOver ? "border-[#1A1A1A] bg-[#EFEBE4]" : "border-[#D5CFC5] bg-white"} p-12 text-center`}
            >
                <input
                    ref={fileInputRef}
                    data-testid="media-file-input"
                    type="file"
                    accept="image/*"
                    multiple
                    hidden
                    onChange={(e) => upload(e.target.files)}
                />
                {uploading ? (
                    <div>
                        <p className="asc-overline">Uploading… {progress}%</p>
                        <div className="mt-4 h-1 bg-[#E5E1DB] overflow-hidden">
                            <div className="h-full bg-[#1A1A1A] transition-all" style={{ width: `${progress}%` }} />
                        </div>
                    </div>
                ) : (
                    <>
                        <p className="serif text-2xl tracking-tight text-[#1A1A1A]">Drop images here</p>
                        <p className="mt-2 text-sm text-[#5C5855]">or click to choose files (multiple supported · max 12 MB each)</p>
                    </>
                )}
            </div>

            {error && <p className="mt-6 text-red-700 text-sm" data-testid="media-error">{error}</p>}

            <div className="mt-10 flex items-center justify-between gap-6 flex-wrap">
                <input
                    data-testid="media-search"
                    type="search"
                    placeholder="Search by filename…"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    className="bg-white border border-[#E5E1DB] focus:border-[#1A1A1A] outline-none px-4 py-2 text-sm w-72"
                />
                <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.18em]">
                    <button
                        data-testid="filter-all"
                        onClick={() => setSourceFilter("")}
                        className={`pb-1 ${sourceFilter === "" ? "border-b border-[#1A1A1A] text-[#1A1A1A]" : "text-[#8C7A6B]"}`}
                    >All</button>
                    <button
                        data-testid="filter-upload"
                        onClick={() => setSourceFilter("upload")}
                        className={`pb-1 ${sourceFilter === "upload" ? "border-b border-[#1A1A1A] text-[#1A1A1A]" : "text-[#8C7A6B]"}`}
                    >Uploaded</button>
                    <button
                        data-testid="filter-external"
                        onClick={() => setSourceFilter("external")}
                        className={`pb-1 ${sourceFilter === "external" ? "border-b border-[#1A1A1A] text-[#1A1A1A]" : "text-[#8C7A6B]"}`}
                    >External</button>
                </div>
            </div>

            {media == null ? (
                <p className="mt-12 asc-overline" data-testid="media-loading">Loading…</p>
            ) : media.length === 0 ? (
                <p className="mt-12 text-[#5C5855]" data-testid="media-empty">No media yet — drop some images above.</p>
            ) : (
                <div data-testid="media-grid" className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-5">
                    {media.map((m) => (
                        <div
                            key={m.id}
                            data-testid={`media-item-${m.id}`}
                            className="bg-white border border-[#E5E1DB] hover:border-[#1A1A1A] transition-colors group"
                        >
                            <div className="aspect-square bg-[#EFEBE4] overflow-hidden">
                                <img src={mediaUrl(m.url)} alt={m.filename} loading="lazy" className="w-full h-full object-cover" />
                            </div>
                            <div className="p-3">
                                <p className="text-xs text-[#1A1A1A] truncate" title={m.filename}>{m.filename}</p>
                                <p className="text-[10px] text-[#8C7A6B] mt-1">
                                    {m.width && m.height ? `${m.width}×${m.height}` : "—"}
                                    {m.size ? ` · ${Math.round(m.size / 1024)} KB` : ""}
                                </p>
                                <p className="text-[10px] text-[#8C7A6B]">{m.source} · {new Date(m.uploaded_at).toLocaleDateString()}</p>
                                <div className="mt-3 flex items-center justify-between">
                                    <button
                                        onClick={() => { navigator.clipboard.writeText(mediaUrl(m.url)); }}
                                        data-testid={`media-copy-${m.id}`}
                                        className="text-[10px] uppercase tracking-[0.18em] text-[#5C5855] hover:text-[#1A1A1A]"
                                    >Copy URL</button>
                                    <button
                                        onClick={() => onDelete(m)}
                                        data-testid={`media-delete-${m.id}`}
                                        className="text-[10px] uppercase tracking-[0.18em] text-red-700 hover:text-red-900"
                                    >Delete</button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </main>
    );
}
